import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field, CheckInput } from './DialogShell';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Button } from '../ui/button';
import { ArrowRight, RotateCcw, Undo2, Palette } from 'lucide-react';
import { toast } from 'sonner';
import { SCHEME_PRESETS, STANDARD_GRID, normalizeHex, isValidHex, hexToHsl, hslToHex, tint, shade, contrastText } from '../../lib/colors';
import { recolorProject } from '../../lib/templates';

const FROM_ID = '__edit_scheme_from__';
const TO_ID = '__edit_scheme_to__';
const ROLE_NAMES = ['Text 1', 'Accent 1', 'Accent 2', 'Accent 3', 'Accent 4', 'Accent 5', 'Text 2', 'Background 1'];
const norm = (hex) => normalizeHex(hex).toLowerCase();

/**
 * Objects of `pages` / `masters` that use the `from` colours (and their tints / shades) take the matching `to` colours.
 * Pure black / white are never remapped (like every scheme change). Returns null when the mapping is empty.
 */
const recolourDocument = (pages, masters, from, to) => {
  SCHEME_PRESETS[FROM_ID] = from.map((c) => ({ ...c }));
  SCHEME_PRESETS[TO_ID] = to.map((c) => ({ ...c }));
  try {
    const next = recolorProject({ pages, masters }, FROM_ID, TO_ID);
    return next.pages === pages && next.masters === masters ? null : { pages: next.pages, masters: next.masters };
  } finally {
    delete SCHEME_PRESETS[FROM_ID];
    delete SCHEME_PRESETS[TO_ID];
  }
};

/**
 * Edit Colour Scheme (Publisher: Page Design ▸ Schemes ▸ Custom scheme ▸ Edit Scheme…): change any of the eight colours of one of
 * the publication's own schemes and rename it. The publication follows live — objects (fills, outlines, text, WordArt, inline
 * HTML) that use an old colour or one of its tints / shades take the new one — and the whole edit is one undoable step
 * (a snapshot is recorded when the dialog opens; Cancel restores everything).
 */
export const EditSchemeDialog = ({ props }) => {
  const { state, dispatch, closeDialog } = useEditor();
  const scheme = state.customSchemes.find((c) => c.id === props.id);
  // Everything is measured from the state at open, so each preview maps original → draft (no drift while dragging).
  const orig = useRef(null);
  if (!orig.current && scheme) {
    orig.current = {
      name: scheme.name,
      colors: scheme.colors.map((c, i) => ({ name: c.name || ROLE_NAMES[i], hex: norm(c.hex) })),
      pages: state.pages,
      masters: state.masters,
      active: state.schemeId === scheme.id,
    };
  }
  const o = orig.current;
  const [name, setName] = useState(o?.name || '');
  const [draft, setDraft] = useState(() => (o ? o.colors.map((c) => c.hex) : []));
  const [sel, setSel] = useState(1); // Accent 1 first — Text 1 / Background 1 are rarely what one wants to change
  const [recolor, setRecolor] = useState(!!o?.active);
  const [hexInput, setHexInput] = useState(o ? o.colors[1].hex : '');
  const timer = useRef(null);
  const previewed = useRef(false); // whether the document currently shows a preview (needs restoring on Cancel / toggle)

  // One history snapshot for the whole edit (Undo afterwards steps back to the state before the dialog opened).
  useEffect(() => { dispatch({ type: 'PUSH_HISTORY' }); }, [dispatch]);
  // keep the hex field in step with the swatch unless it already spells the same colour (typing "#abc" must not jump to "#aabbcc")
  useEffect(() => { setHexInput((h) => (isValidHex(h) && norm(h) === draft[sel] ? h : draft[sel] || '')); }, [sel, draft]);

  const changed = useMemo(() => (o ? draft.map((h, i) => h !== o.colors[i].hex) : []), [draft, o]);
  const anyChanged = changed.some(Boolean);
  const tints = useMemoTints(o ? o.colors[sel].hex : '#000000');

  // Live preview (throttled): scheme colours for every picker / chip, then the objects that use the old colours.
  useEffect(() => {
    if (!o || !scheme) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      timer.current = null;
      const colors = o.colors.map((c, i) => ({ name: c.name, hex: draft[i] }));
      dispatch({ type: 'UPDATE_CUSTOM_SCHEME', payload: { id: scheme.id, colors } });
      if (recolor && anyChanged) {
        const next = recolourDocument(o.pages, o.masters, o.colors, colors);
        if (next) { dispatch({ type: 'PREVIEW_DOCUMENT_CONTENT', payload: next }); previewed.current = true; return; }
      }
      if (previewed.current) { dispatch({ type: 'PREVIEW_DOCUMENT_CONTENT', payload: { pages: o.pages, masters: o.masters } }); previewed.current = false; }
    }, 40);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // deliberately keyed on the user's inputs only: `scheme` is what this effect writes (UPDATE_CUSTOM_SCHEME) — listing it
    // would make the preview re-trigger itself; `o` is the open-time snapshot (ref), anyChanged derives from draft, dispatch is stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, recolor]);

  if (!scheme || !o) {
    return (
      <WpDialog name="edit-scheme" title="Edit Colour Scheme" width="w-[420px]" onCancel={closeDialog} cancelLabel="Close">
        <p className="text-sm text-muted-foreground">This colour scheme no longer exists.</p>
      </WpDialog>
    );
  }

  const setColor = (i, hex) => { if (!isValidHex(hex)) return; const n = norm(hex); setDraft((d) => (d[i] === n ? d : d.map((x, k) => (k === i ? n : x)))); };
  const cur = draft[sel];
  const hsl = hexToHsl(cur);

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const colors = o.colors.map((c, i) => ({ name: c.name, hex: draft[i] }));
    dispatch({ type: 'UPDATE_CUSTOM_SCHEME', payload: { id: scheme.id, name: name.trim() || o.name, colors } });
    if (recolor && anyChanged) {
      const next = recolourDocument(o.pages, o.masters, o.colors, colors);
      if (next) { dispatch({ type: 'PREVIEW_DOCUMENT_CONTENT', payload: next }); previewed.current = true; return; }
    }
    if (previewed.current) { dispatch({ type: 'PREVIEW_DOCUMENT_CONTENT', payload: { pages: o.pages, masters: o.masters } }); previewed.current = false; }
  };

  const cancel = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    dispatch({ type: 'UPDATE_CUSTOM_SCHEME', payload: { id: scheme.id, name: o.name, colors: o.colors } });
    if (previewed.current) dispatch({ type: 'PREVIEW_DOCUMENT_CONTENT', payload: { pages: o.pages, masters: o.masters } });
    previewed.current = false;
    dispatch({ type: 'PUSH_HISTORY' }); // identical to the snapshot taken at open → just clears the "dirty" flag
    closeDialog();
  };

  const ok = () => {
    flush();
    const n = changed.filter(Boolean).length;
    const renamed = name.trim() && name.trim() !== o.name;
    toast.success(`Colour scheme “${name.trim() || o.name}” updated`, {
      description: n
        ? `${n} colour${n === 1 ? '' : 's'} changed${previewed.current ? ' — objects using the old colours now use the new ones' : ''}${renamed ? ', renamed' : ''}. Ctrl+Z undoes it.`
        : renamed ? 'Renamed.' : 'No colours changed.',
      icon: previewed.current ? <Undo2 className="h-4 w-4" /> : <Palette className="h-4 w-4" />,
    });
    closeDialog();
  };

  const pickFromArea = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)), y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setColor(sel, hslToHex({ h: hsl.h, s: Math.round(x * 100), l: Math.round((1 - y) * (100 - x * 50)) }));
  };

  return (
    <WpDialog
      name="edit-scheme"
      title="Edit Colour Scheme"
      description="Change any of the scheme's colours — the publication follows as you pick. One Undo step."
      width="w-[680px]"
      onCancel={cancel}
      onOk={ok}
      okLabel="OK"
      okDisabled={!name.trim()}
      extraFooter={anyChanged && <Button variant="ghost" onClick={() => setDraft(o.colors.map((c) => c.hex))} data-testid="edit-scheme-reset-all"><RotateCcw className="h-4 w-4 mr-2" /> Reset all colours</Button>}
    >
      <div className="space-y-4" data-testid="edit-scheme-dialog">
        <Field label="Scheme name"><Input value={name} onChange={(e) => setName(e.target.value)} className="h-8 bg-background border-border" data-testid="edit-scheme-name" /></Field>

        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_236px] gap-4">
          {/* the eight roles: current → new */}
          <div className="rounded-md border border-border overflow-hidden" role="listbox" aria-label="Scheme colours" data-testid="edit-scheme-roles">
            <div className="grid grid-cols-[1fr_28px_16px_28px_auto] items-center gap-x-2 px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/40">
              <span>Role</span><span className="text-center">Now</span><span /><span className="text-center">New</span><span />
            </div>
            {o.colors.map((c, i) => (
              <button
                key={c.name + i}
                type="button"
                role="option"
                aria-selected={sel === i}
                onClick={() => setSel(i)}
                className={`w-full grid grid-cols-[1fr_28px_16px_28px_auto] items-center gap-x-2 px-2 py-1.5 text-left text-[13px] border-t border-border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${sel === i ? 'bg-primary/10' : 'hover:bg-muted/60'}`}
                data-testid={`edit-scheme-role-${i}`}
                data-hex={draft[i]}
              >
                <span className="truncate text-foreground">{c.name}</span>
                <span className="h-6 w-7 rounded-[3px] border border-black/10 dark:border-white/20" style={{ background: c.hex }} title={`Now ${c.hex.toUpperCase()}`} />
                <ArrowRight className={`h-3.5 w-3.5 ${changed[i] ? 'text-primary' : 'text-muted-foreground/40'}`} />
                <span className="h-6 w-7 rounded-[3px] border border-black/10 dark:border-white/20" style={{ background: draft[i] }} title={`New ${draft[i].toUpperCase()}`} />
                <span className="font-mono text-[11px] text-muted-foreground w-[62px] text-right">{changed[i] ? draft[i].toUpperCase() : ''}</span>
              </button>
            ))}
          </div>

          {/* picker for the selected role */}
          <div className="space-y-2.5" data-testid="edit-scheme-picker">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-foreground">{o.colors[sel].name}</span>
              {changed[sel] && <button type="button" onClick={() => setColor(sel, o.colors[sel].hex)} className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground" data-testid="edit-scheme-reset-one"><RotateCcw className="h-3 w-3" /> Reset</button>}
            </div>
            <div className="flex overflow-hidden rounded-md border border-border h-9 text-[10px] font-mono">
              <div className="flex-1 flex items-center justify-center" style={{ background: o.colors[sel].hex, color: contrastText(o.colors[sel].hex) }} title="Now">{o.colors[sel].hex.toUpperCase()}</div>
              <div className="flex-1 flex items-center justify-center" style={{ background: cur, color: contrastText(cur) }} title="New" data-testid="edit-scheme-new-preview">{cur.toUpperCase()}</div>
            </div>
            <div
              className="h-24 w-full rounded-md border border-border cursor-crosshair touch-none"
              style={{ background: `linear-gradient(to bottom, transparent, #000), linear-gradient(to right, #fff, hsl(${hsl.h} 100% 50%))` }}
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); pickFromArea(e); }}
              onPointerMove={(e) => { if (e.buttons & 1) pickFromArea(e); }}
              role="img"
              aria-label="Saturation and lightness picker"
              data-testid="edit-scheme-area"
            />
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground w-8">Hue</span>
              <Slider value={[hsl.h]} min={0} max={360} step={1} onValueChange={([v]) => setColor(sel, hslToHex({ ...hsl, h: v }))} className="flex-1" aria-label="Hue" data-testid="edit-scheme-hue" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground w-8">Hex</span>
              <Input value={hexInput} onChange={(e) => { setHexInput(e.target.value); if (isValidHex(e.target.value)) setColor(sel, e.target.value); }} onBlur={() => setHexInput(cur)} className="h-7 font-mono uppercase text-xs bg-background border-border" data-testid="edit-scheme-hex" />
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Tints and shades of the current colour</div>
              <div className="grid grid-cols-9 gap-[3px]" data-testid="edit-scheme-tints">
                {tints.map((h, k) => <button key={`${h}-${k}`} type="button" onClick={() => setColor(sel, h)} title={h.toUpperCase()} className={`h-5 rounded-[2px] border border-black/10 dark:border-white/20 hover:scale-110 transition-transform ${cur === h ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}`} style={{ background: h }} data-testid={`edit-scheme-tint-${k}`} />)}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-muted-foreground mb-1">Standard colours</div>
              <div className="space-y-[3px]" data-testid="edit-scheme-standard">
                {STANDARD_GRID.filter((_, r) => r % 2 === 1 || r === STANDARD_GRID.length - 1).map((row, r) => (
                  <div key={r} className="grid grid-cols-12 gap-[3px]">{row.map((h, k) => <button key={h + k} type="button" onClick={() => setColor(sel, h)} title={h.toUpperCase()} className={`h-4 rounded-[2px] border border-black/10 dark:border-white/20 hover:scale-110 transition-transform ${cur === norm(h) ? 'ring-2 ring-primary ring-offset-1 ring-offset-card' : ''}`} style={{ background: h }} data-testid={`edit-scheme-std-${r}-${k}`} />)}</div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <CheckInput checked={recolor} onChange={setRecolor} label={o.active ? 'Recolour the publication: objects using the old colours (and their tints / shades) take the new ones' : 'Recolour the publication too (this scheme is not the active one, so objects may not use its colours)'} testId="edit-scheme-recolor" />
        <p className="text-[11px] text-muted-foreground">Pure black and white are never remapped, so changing Text 1 or Background 1 only affects the scheme itself. Cancel puts everything back.</p>
      </div>
    </WpDialog>
  );
};

/** 4 tints, the base, 4 shades of `hex` (stable per base colour). */
const useMemoTints = (hex) => useMemo(() => [0.8, 0.6, 0.4, 0.2].map((a) => norm(tint(hex, a))).concat([norm(hex)], [0.15, 0.3, 0.45, 0.6].map((a) => norm(shade(hex, a)))), [hex]);
